import React, { useState, useEffect, useCallback } from "react";
import { FiPlus, FiTrash2, FiEdit2, FiChevronDown, FiChevronRight, FiPackage, FiPower, FiBox } from "react-icons/fi";
import { HiOutlineSparkles } from "react-icons/hi2";
import Modal from "react-modal";
import ConfirmModal from "../../Components/ConfirmModal";
import { uploadImageToS3Bucket, optimizeAndConvertToWebp } from "../../services/imageOptimizerService";
import { createCatogory, getCategories, updateCategory, deleteCategory, toggleCategoryProductsActive, toggleCategoryProductsStock, getCategoryProductCounts } from "../../services/ClientService";
import { BiSolidCategory } from "react-icons/bi";
import { MdCategory, MdOutlineInventory } from "react-icons/md";
import { toast } from "react-toastify";
import { adminFetchJson } from "../../services/apiService";

// Custom Toggle Switch Component
const ToggleSwitch = ({ isOn, onToggle, disabled, label, colorOn = "bg-emerald-500", colorOff = "bg-gray-300", icon }) => (
    <div className="flex items-center gap-2">
        {icon && <span className="text-gray-500">{icon}</span>}
        {label && <span className="text-xs font-medium text-gray-600 min-w-[60px]">{label}</span>}
        <button
            onClick={onToggle}
            disabled={disabled}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:shadow-md"
                } ${isOn ? colorOn : colorOff}`}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform duration-300 ease-in-out ${isOn ? "translate-x-6" : "translate-x-1"
                    }`}
            />
        </button>
    </div>
);

// Stats Badge Component
const StatsBadge = ({ count, label, color = "blue" }) => {
    const colorClasses = {
        blue: "bg-blue-100 text-blue-700 border-blue-200",
        green: "bg-emerald-100 text-emerald-700 border-emerald-200",
        red: "bg-red-100 text-red-700 border-red-200",
        orange: "bg-orange-100 text-orange-700 border-orange-200",
        purple: "bg-purple-100 text-purple-700 border-purple-200"
    };

    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${colorClasses[color]}`}>
            {count} {label}
        </span>
    );
};

const CategoryManagement = () => {
    const [categories, setCategories] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({ name: "", image: null, parentCategoryId: null, hideDeliveryTimeLabel: false, enableCOD: true, isPreOrder: false, baseDeliveryTimeValue: "", baseDeliveryTimeUnit: "days" });
    const [errors, setErrors] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [currentCategoryId, setCurrentCategoryId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [expandedCategories, setExpandedCategories] = useState(new Set());
    const [productCounts, setProductCounts] = useState({});
    const [togglingCategory, setTogglingCategory] = useState(null);
    const [branchCODEnabled, setBranchCODEnabled] = useState(true); // Global branch COD setting

    // Fetch branch data to check global COD setting
    useEffect(() => {
        const fetchBranchCODSetting = async () => {
            try {
                const userData = JSON.parse(localStorage.getItem("userData"));
                if (!userData) return;

                const { data } = await adminFetchJson('/clients/getBranchNamesForUser', {
                    method: 'POST',
                    body: {},
                });
                if (data.success && data.data) {
                    // For role 1: use selectedBranch, for role 2: use their own _id
                    const branchIdToCheck = userData.role === 1
                        ? userData.selectedBranch
                        : userData._id;

                    const selectedBranch = data.data.find(
                        (b) => userData.role === 1
                            ? b.masterId === branchIdToCheck
                            : b._id === branchIdToCheck
                    );

                    if (selectedBranch) {
                        // Set the global COD setting from branch
                        setBranchCODEnabled(selectedBranch.cod === true);
                    }
                }
            } catch (error) {
                console.error("Error fetching branch COD setting:", error);
            }
        };

        fetchBranchCODSetting();
    }, []);

    const fetchCategories = useCallback(async () => {
        setLoading(true);
        try {
            const [categoriesData, countsData] = await Promise.all([
                getCategories({}),
                getCategoryProductCounts({})
            ]);

            if (categoriesData.success) {
                setCategories(categoriesData.data);
            }
            if (countsData.success) {
                setProductCounts(countsData.data);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            toast.error("Failed to fetch categories");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Category name is required.";
        if (!formData.image) newErrors.image = "Category image is required.";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        try {
            setLoading(true);

            let optimizedImageUrl = formData.image;
            if (formData.image && typeof formData.image === "object") {
                const optimizedFile = await optimizeAndConvertToWebp(formData.image);
                const fileName = `category_${Date.now()}.webp`;
                const fileType = "image/webp";
                optimizedImageUrl = await uploadImageToS3Bucket(optimizedFile, fileType, fileName);
            }

            const payload = {
                categoryName: formData.name,
                categoryImage: optimizedImageUrl,
                parentCategoryId: formData.parentCategoryId || undefined,
                hideDeliveryTimeLabel: formData.hideDeliveryTimeLabel,
                enableCOD: branchCODEnabled ? formData.enableCOD : false, // Force false if branch COD is disabled
                isPreOrder: formData.isPreOrder,
                baseDeliveryTimeValue: formData.isPreOrder ? Number(formData.baseDeliveryTimeValue) || null : null,
                baseDeliveryTimeUnit: formData.isPreOrder ? formData.baseDeliveryTimeUnit : null,
                _id: isEditing ? currentCategoryId : undefined,
            };

            const data = isEditing
                ? await updateCategory(payload)
                : await createCatogory(payload);

            if (data.success) {
                toast.success(isEditing ? "Category updated successfully!" : "Category created successfully!");
                fetchCategories();
                resetModal();
            }
        } catch (error) {
            console.error("Error saving category:", error);
            toast.error("Failed to save category");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (category) => {
        setFormData({
            name: category.categoryName,
            image: category.imageUrl,
            parentCategoryId: category.parentCategoryId || null,
            hideDeliveryTimeLabel: category.hideDeliveryTimeLabel || false,
            enableCOD: category.enableCOD !== false, // Default to true for backward compatibility
            isPreOrder: category.isPreOrder || false,
            baseDeliveryTimeValue: category.baseDeliveryTimeValue || "",
            baseDeliveryTimeUnit: category.baseDeliveryTimeUnit || "days",
        });
        setIsEditing(true);
        setCurrentCategoryId(category._id);
        setIsModalOpen(true);
    };

    const handleAddSubcategory = (parentId) => {
        setFormData({
            name: "",
            image: null,
            parentCategoryId: parentId,
            hideDeliveryTimeLabel: false,
            enableCOD: true,
            isPreOrder: false,
            baseDeliveryTimeValue: "",
            baseDeliveryTimeUnit: "days",
        });
        setIsEditing(false);
        setCurrentCategoryId(null);
        setIsModalOpen(true);
    };

    const toggleCategory = (categoryId) => {
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(categoryId)) {
                newSet.delete(categoryId);
            } else {
                newSet.add(categoryId);
            }
            return newSet;
        });
    };

    const handleDeleteRequest = (categoryId) => {
        setCategoryToDelete(categoryId);
        setConfirmModalVisible(true);
    };

    const confirmDeleteCategory = async () => {
        try {
            setLoading(true);
            const data = await deleteCategory({ _id: categoryToDelete });
            if (data.success) {
                setCategories((prev) => {
                    const filtered = prev.filter((category) => category._id !== categoryToDelete);
                    return filtered.map((category) => {
                        if (category.subcategories && category.subcategories.length > 0) {
                            return {
                                ...category,
                                subcategories: category.subcategories.filter(
                                    (sub) => sub._id !== categoryToDelete
                                ),
                            };
                        }
                        return category;
                    });
                });
                toast.success("Category deleted successfully!");
            } else {
                // Show server-provided error when available so user understands why delete failed
                const serverMsg = (data && (data.error || data.message)) ? (data.error || data.message) : "Failed to delete category";
                toast.error(serverMsg);
            }
        } catch (error) {
            console.error("Error deleting category:", error);
            // Prefer server-provided message when available
            const errMsg = (error && (error.message || error.error)) ? (error.message || error.error) : (typeof error === 'string' ? error : "Failed to delete category");
            toast.error(errMsg);
        } finally {
            setLoading(false);
            setConfirmModalVisible(false);
            setCategoryToDelete(null);
        }
    };

    const handleToggleActive = async (categoryId, currentStatus, isSubcategory = false) => {
        setTogglingCategory(categoryId);
        try {
            const newStatus = !currentStatus;
            const result = await toggleCategoryProductsActive({
                categoryId,
                isActive: newStatus,
                includeSubcategories: !isSubcategory
            });

            if (result.success) {
                toast.success(result.message);
                const countsData = await getCategoryProductCounts({});
                if (countsData.success) {
                    setProductCounts(countsData.data);
                }
            } else {
                toast.error("Failed to update product status");
            }
        } catch (error) {
            console.error("Error toggling active status:", error);
            toast.error("Failed to update product status");
        } finally {
            setTogglingCategory(null);
        }
    };

    const handleToggleStock = async (categoryId, currentStatus, isSubcategory = false) => {
        setTogglingCategory(categoryId);
        try {
            const newStatus = !currentStatus;
            const result = await toggleCategoryProductsStock({
                categoryId,
                isOutOfStock: newStatus,
                includeSubcategories: !isSubcategory
            });

            if (result.success) {
                toast.success(result.message);
                const countsData = await getCategoryProductCounts({});
                if (countsData.success) {
                    setProductCounts(countsData.data);
                }
            } else {
                toast.error("Failed to update stock status");
            }
        } catch (error) {
            console.error("Error toggling stock status:", error);
            toast.error("Failed to update stock status");
        } finally {
            setTogglingCategory(null);
        }
    };

    const resetModal = () => {
        setIsModalOpen(false);
        setFormData({ name: "", image: null, parentCategoryId: null, hideDeliveryTimeLabel: false, enableCOD: true, isPreOrder: false, baseDeliveryTimeValue: "", baseDeliveryTimeUnit: "days" });
        setErrors({});
        setIsEditing(false);
        setCurrentCategoryId(null);
    };

    const getCategoryStats = (categoryId, subcategories = []) => {
        let stats = productCounts[categoryId] || { totalProducts: 0, activeProducts: 0, inStockProducts: 0, outOfStockProducts: 0, allActive: true, allInStock: true };

        if (subcategories.length > 0) {
            subcategories.forEach(sub => {
                const subStats = productCounts[sub._id] || { totalProducts: 0, activeProducts: 0, inStockProducts: 0, outOfStockProducts: 0 };
                stats = {
                    totalProducts: stats.totalProducts + subStats.totalProducts,
                    activeProducts: stats.activeProducts + subStats.activeProducts,
                    inStockProducts: stats.inStockProducts + subStats.inStockProducts,
                    outOfStockProducts: stats.outOfStockProducts + subStats.outOfStockProducts,
                    allActive: stats.allActive && (subStats.totalProducts === 0 || subStats.activeProducts === subStats.totalProducts),
                    allInStock: stats.allInStock && subStats.outOfStockProducts === 0
                };
            });
        }

        return stats;
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
            {loading && !categories.length ? (
                <div className="fixed inset-0 flex justify-center items-center bg-white/80 z-50 backdrop-blur-sm">
                    <div className="flex flex-col items-center gap-4">
                        <div className="relative">
                            <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
                            <BiSolidCategory className="absolute inset-0 m-auto text-2xl text-blue-600" />
                        </div>
                        <p className="text-gray-600 font-medium">Loading categories...</p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Header Section */}
                    <div className="mb-8">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div>
                                <h1 className="text-4xl font-bold text-blue-700">
                                    Categories
                                </h1>
                                <p className="text-gray-500 mt-1 flex items-center gap-2">
                                    <HiOutlineSparkles className="text-yellow-500" />
                                    Manage your product categories and control bulk product settings
                                </p>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="group relative overflow-hidden text-white font-semibold bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl px-6 py-3 transition-all duration-300 ease-out transform hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/25"
                            >
                                <span className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                                <span className="relative z-10 flex items-center gap-2">
                                    <BiSolidCategory className="text-xl" />
                                    Add Category
                                </span>
                            </button>
                        </div>

                        {/* Stats Overview */}
                        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-white/50 shadow-lg shadow-indigo-500/5">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                                        <BiSolidCategory className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-gray-800">{categories.length}</p>
                                        <p className="text-sm text-gray-500">Categories</p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-white/50 shadow-lg shadow-purple-500/5">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                                        <MdCategory className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-gray-800">
                                            {categories.reduce((acc, cat) => acc + (cat.subcategories?.length || 0), 0)}
                                        </p>
                                        <p className="text-sm text-gray-500">Subcategories</p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-white/50 shadow-lg shadow-emerald-500/5">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                                        <FiPackage className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-gray-800">
                                            {Object.values(productCounts).reduce((acc, cat) => acc + (cat.totalProducts || 0), 0)}
                                        </p>
                                        <p className="text-sm text-gray-500">Total Products</p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-white/50 shadow-lg shadow-orange-500/5">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/30">
                                        <MdOutlineInventory className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-gray-800">
                                            {Object.values(productCounts).reduce((acc, cat) => acc + (cat.activeProducts || 0), 0)}
                                        </p>
                                        <p className="text-sm text-gray-500">Active Products</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Categories Grid */}
                    {categories.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center mb-6">
                                <BiSolidCategory className="text-5xl text-indigo-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Categories Yet</h3>
                            <p className="text-gray-500 mb-6">Start by creating your first category</p>
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition-shadow"
                            >
                                Create Category
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                            {categories.map((category) => {
                                const stats = getCategoryStats(category._id, category.subcategories || []);
                                const isToggling = togglingCategory === category._id;

                                return (
                                    <div key={category._id} className="space-y-4">
                                        {/* Parent Category Card */}
                                        <div className="group bg-white/80 backdrop-blur-sm rounded-3xl overflow-hidden border border-white/50 shadow-xl shadow-indigo-500/5 hover:shadow-2xl hover:shadow-indigo-500/10 transition-all duration-500 hover:-translate-y-1">
                                            {/* Card Header with Image */}
                                            <div className="relative h-40 bg-gradient-to-br from-indigo-300 via-purple-200 to-pink-100 overflow-hidden">
                                                <div className="absolute inset-0 bg-black/20"></div>
                                                <img
                                                    src={category.imageUrl}
                                                    alt={category.categoryName}
                                                    className="w-full h-full object-cover mix-blend-overlay opacity-60"
                                                />
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <div className="w-28 h-28 rounded-2xl overflow-hidden shadow-2xl border-4 border-white/40 backdrop-blur-sm">
                                                        <img
                                                            src={category.imageUrl}
                                                            alt={category.categoryName}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Action Buttons - Top Right */}
                                                <div className="absolute top-3 right-3 flex gap-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                    <button
                                                        onClick={() => handleEdit(category)}
                                                        className="w-9 h-9 bg-white/90 backdrop-blur-sm text-blue-600 rounded-xl shadow-lg hover:bg-blue-500 hover:text-white transition-all duration-200 flex items-center justify-center"
                                                        title="Edit Category"
                                                    >
                                                        <FiEdit2 size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteRequest(category._id)}
                                                        className="w-9 h-9 bg-white/90 backdrop-blur-sm text-red-600 rounded-xl shadow-lg hover:bg-red-500 hover:text-white transition-all duration-200 flex items-center justify-center"
                                                        title="Delete Category"
                                                    >
                                                        <FiTrash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Card Body */}
                                            <div className="p-5">
                                                {/* Title and Subcategory Toggle */}
                                                <div className="flex items-center justify-between mb-4">
                                                    <div>
                                                        <h2 className="text-xl font-bold text-gray-800">{category.categoryName}</h2>
                                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                            <span className="text-sm text-gray-500">
                                                                {category.subcategories?.length || 0} subcategories
                                                            </span>
                                                            {category.subcategories?.length > 0 && (
                                                                <button
                                                                    onClick={() => toggleCategory(category._id)}
                                                                    className="text-indigo-500 hover:text-indigo-700 p-1 hover:bg-indigo-50 rounded-lg transition-colors"
                                                                >
                                                                    {expandedCategories.has(category._id) ? (
                                                                        <FiChevronDown size={18} />
                                                                    ) : (
                                                                        <FiChevronRight size={18} />
                                                                    )}
                                                                </button>
                                                            )}
                                                        </div>
                                                        {category.isPreOrder && (
                                                            <span className="inline-flex items-center px-2 py-0.5 mt-2 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-200">
                                                                📦 Pre-Order {category.baseDeliveryTimeValue ? `(${category.baseDeliveryTimeValue} ${category.baseDeliveryTimeUnit})` : ''}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <button
                                                        onClick={() => handleAddSubcategory(category._id)}
                                                        className="flex items-center gap-1 px-3 py-2 bg-gradient-to-r from-emerald-400 to-teal-500 text-white rounded-xl text-sm font-medium shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:shadow-emerald-500/30 transition-all duration-200 hover:-translate-y-0.5"
                                                        title="Add Subcategory"
                                                    >
                                                        <FiPlus size={16} />
                                                        <span>Add Sub</span>
                                                    </button>
                                                </div>

                                                {/* Product Stats */}
                                                {stats.totalProducts > 0 && (
                                                    <div className="flex flex-wrap gap-2 mb-4">
                                                        <StatsBadge count={stats.totalProducts} label="products" color="purple" />
                                                        <StatsBadge count={stats.activeProducts} label="active" color="green" />
                                                        {stats.outOfStockProducts > 0 && (
                                                            <StatsBadge count={stats.outOfStockProducts} label="out of stock" color="orange" />
                                                        )}
                                                    </div>
                                                )}

                                                {/* Toggle Controls */}
                                                <div className="space-y-3 pt-4 border-t border-gray-100">
                                                    <div className="flex items-center justify-between bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl p-3">
                                                        <div className="flex items-center gap-2">
                                                            <FiPower className={`text-lg ${stats.allActive ? 'text-emerald-500' : 'text-gray-400'}`} />
                                                            <span className="text-sm font-medium text-gray-700">Products Active</span>
                                                        </div>
                                                        <ToggleSwitch
                                                            isOn={stats.allActive}
                                                            onToggle={() => handleToggleActive(category._id, stats.allActive)}
                                                            disabled={isToggling || stats.totalProducts === 0}
                                                            colorOn="bg-emerald-500"
                                                            colorOff="bg-gray-300"
                                                        />
                                                    </div>
                                                    <div className="flex items-center justify-between bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl p-3">
                                                        <div className="flex items-center gap-2">
                                                            <FiBox className={`text-lg ${stats.allInStock ? 'text-blue-500' : 'text-orange-400'}`} />
                                                            <span className="text-sm font-medium text-gray-700">Products In Stock</span>
                                                        </div>
                                                        <ToggleSwitch
                                                            isOn={stats.allInStock}
                                                            onToggle={() => handleToggleStock(category._id, !stats.allInStock)}
                                                            disabled={isToggling || stats.totalProducts === 0}
                                                            colorOn="bg-blue-500"
                                                            colorOff="bg-orange-400"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Subcategories (Collapsible) */}
                                        {expandedCategories.has(category._id) && category.subcategories?.length > 0 && (
                                            <div className="ml-6 space-y-3 pl-4 border-l-4 border-indigo-300">
                                                {category.subcategories.map((subcategory) => {
                                                    const subStats = productCounts[subcategory._id] || { totalProducts: 0, activeProducts: 0, outOfStockProducts: 0, allActive: true, allInStock: true };
                                                    const isSubToggling = togglingCategory === subcategory._id;

                                                    return (
                                                        <div
                                                            key={subcategory._id}
                                                            className="group bg-white/70 backdrop-blur-sm rounded-2xl p-4 shadow-lg shadow-purple-500/5 hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300 border border-white/50"
                                                        >
                                                            {/* Subcategory Header */}
                                                            <div className="flex items-center gap-4 mb-3">
                                                                <div className="w-14 h-14 rounded-xl overflow-hidden shadow-lg ring-2 ring-purple-200">
                                                                    <img
                                                                        src={subcategory.imageUrl}
                                                                        alt={subcategory.categoryName}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                </div>
                                                                <div className="flex-1">
                                                                    <h3 className="text-base font-semibold text-gray-700">
                                                                        {subcategory.categoryName}
                                                                    </h3>
                                                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                                        <span className="text-xs text-purple-500 flex items-center gap-1 bg-purple-50 px-2 py-0.5 rounded-full">
                                                                            <MdCategory /> Subcategory
                                                                        </span>
                                                                        {subcategory.isPreOrder && (
                                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700 border border-blue-200">
                                                                                📦 Pre-Order {subcategory.baseDeliveryTimeValue ? `(${subcategory.baseDeliveryTimeValue} ${subcategory.baseDeliveryTimeUnit})` : ''}
                                                                            </span>
                                                                        )}
                                                                        {subStats.totalProducts > 0 && (
                                                                            <span className="text-xs text-gray-500">
                                                                                {subStats.totalProducts} products
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                    <button
                                                                        onClick={() => handleEdit(subcategory)}
                                                                        className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-500 hover:text-white transition-all flex items-center justify-center"
                                                                    >
                                                                        <FiEdit2 size={14} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDeleteRequest(subcategory._id)}
                                                                        className="w-8 h-8 bg-red-100 text-red-600 rounded-lg hover:bg-red-500 hover:text-white transition-all flex items-center justify-center"
                                                                    >
                                                                        <FiTrash2 size={14} />
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            {/* Subcategory Toggle Controls */}
                                                            <div className="flex flex-wrap gap-4 pt-3 border-t border-gray-100">
                                                                <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                                                                    <ToggleSwitch
                                                                        isOn={subStats.allActive}
                                                                        onToggle={() => handleToggleActive(subcategory._id, subStats.allActive, true)}
                                                                        disabled={isSubToggling || subStats.totalProducts === 0}
                                                                        label="Active"
                                                                        colorOn="bg-emerald-500"
                                                                        icon={<FiPower size={14} />}
                                                                    />
                                                                </div>
                                                                <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                                                                    <ToggleSwitch
                                                                        isOn={subStats.allInStock}
                                                                        onToggle={() => handleToggleStock(subcategory._id, !subStats.allInStock, true)}
                                                                        disabled={isSubToggling || subStats.totalProducts === 0}
                                                                        label="In Stock"
                                                                        colorOn="bg-blue-500"
                                                                        colorOff="bg-orange-400"
                                                                        icon={<FiBox size={14} />}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Modal for Create/Edit Category */}
                    <Modal
                        isOpen={isModalOpen}
                        onRequestClose={!loading ? resetModal : undefined}
                        className="bg-white rounded-3xl shadow-2xl max-w-4xl mx-auto relative overflow-hidden h-[90vh] flex flex-col"
                        overlayClassName="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
                    >
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 p-6 text-white flex-shrink-0">
                            <h2 className="text-2xl font-bold flex items-center gap-3">
                                {isEditing ? <FiEdit2 /> : <FiPlus />}
                                {isEditing
                                    ? `Edit ${formData.parentCategoryId ? 'Subcategory' : 'Category'}`
                                    : `Add ${formData.parentCategoryId ? 'Subcategory' : 'Category'}`}
                            </h2>
                            {formData.parentCategoryId && !isEditing && (
                                <p className="text-sm text-white/80 mt-2 flex items-center gap-2">
                                    <MdCategory />
                                    Creating under: <strong>{categories.find(c => c._id === formData.parentCategoryId)?.categoryName}</strong>
                                </p>
                            )}
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-6 overflow-y-auto flex-1">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    {formData.parentCategoryId ? 'Subcategory' : 'Category'} Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                                    placeholder="Enter category name..."
                                    disabled={loading}
                                />
                                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    {formData.parentCategoryId ? 'Subcategory' : 'Category'} Image
                                </label>
                                <div className="relative">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setFormData((prev) => ({ ...prev, image: e.target.files[0] }))}
                                        className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                                        disabled={loading}
                                    />
                                </div>
                                {formData.image && (
                                    <div className="mt-3 flex justify-center">
                                        <div className="w-24 h-24 rounded-xl overflow-hidden shadow-lg ring-4 ring-purple-100">
                                            <img
                                                src={typeof formData.image === "string" ? formData.image : URL.createObjectURL(formData.image)}
                                                alt="Preview"
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    </div>
                                )}
                                {errors.image && <p className="text-red-500 text-sm mt-1">{errors.image}</p>}
                            </div>

                            {/* Hide Delivery Time Label Toggle */}
                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700">
                                            Hide Delivery Time Label
                                        </label>
                                        <p className="text-xs text-gray-500 mt-1">
                                            When enabled, products in this {formData.parentCategoryId ? 'subcategory' : 'category'} will not display estimated delivery time
                                        </p>
                                    </div>
                                    <ToggleSwitch
                                        isOn={formData.hideDeliveryTimeLabel}
                                        onToggle={() => setFormData((prev) => ({ ...prev, hideDeliveryTimeLabel: !prev.hideDeliveryTimeLabel }))}
                                        disabled={loading}
                                        colorOn="bg-orange-500"
                                        colorOff="bg-gray-300"
                                    />
                                </div>
                            </div>

                            {/* Pre-Order Category Toggle */}
                            <div className={`bg-gray-50 rounded-xl p-4 border ${formData.isPreOrder ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700">
                                            Pre-Order Category
                                        </label>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Enable for categories like Sea Food where items need to be sourced from vendors before delivery.
                                            The base delivery time will be added to the branch-to-customer delivery time.
                                        </p>
                                    </div>
                                    <ToggleSwitch
                                        isOn={formData.isPreOrder}
                                        onToggle={() => setFormData((prev) => ({ ...prev, isPreOrder: !prev.isPreOrder }))}
                                        disabled={loading}
                                        colorOn="bg-blue-500"
                                        colorOff="bg-gray-300"
                                    />
                                </div>

                                {/* Base Delivery Time Configuration - Only show when Pre-Order is enabled */}
                                {formData.isPreOrder && (
                                    <div className="mt-4 pt-4 border-t border-blue-200">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Base Delivery Time (Vendor to Branch)
                                        </label>
                                        <p className="text-xs text-gray-500 mb-3">
                                            Time taken for the vendor to pack and deliver items to your branch location.
                                            This time will be shown to customers as: Base Time + Branch-to-Customer Time (e.g., 6 Days + 2 Hours)
                                        </p>
                                        <div className="flex gap-3">
                                            <div className="flex-1">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={formData.baseDeliveryTimeValue}
                                                    onChange={(e) => setFormData((prev) => ({ ...prev, baseDeliveryTimeValue: e.target.value }))}
                                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                                    placeholder="e.g., 6"
                                                    disabled={loading}
                                                />
                                            </div>
                                            <div className="w-40">
                                                <select
                                                    value={formData.baseDeliveryTimeUnit}
                                                    onChange={(e) => setFormData((prev) => ({ ...prev, baseDeliveryTimeUnit: e.target.value }))}
                                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                                                    disabled={loading}
                                                >
                                                    <option value="minutes">Minutes</option>
                                                    <option value="hours">Hours</option>
                                                    <option value="days">Days</option>
                                                </select>
                                            </div>
                                        </div>
                                        {formData.isPreOrder && formData.baseDeliveryTimeValue && (
                                            <div className="mt-3 bg-blue-100 rounded-lg p-3 flex items-center gap-2">
                                                <span className="text-blue-600 text-lg">📦</span>
                                                <p className="text-sm text-blue-700 font-medium">
                                                    Delivery time shown to customers: <strong>{formData.baseDeliveryTimeValue} {formData.baseDeliveryTimeUnit}</strong> + Branch-to-Customer Time
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Enable Cash on Delivery (COD) Toggle */}
                            <div className={`bg-gray-50 rounded-xl p-4 border ${!branchCODEnabled ? 'border-orange-200 bg-orange-50' : 'border-gray-200'}`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700">
                                            Enable Cash on Delivery (COD)
                                        </label>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {!branchCODEnabled
                                                ? 'COD is disabled globally in branch settings. Enable it in Create/Edit Branch to use this option.'
                                                : `When disabled, products in this ${formData.parentCategoryId ? 'subcategory' : 'category'} will not be eligible for Cash on Delivery payment`
                                            }
                                        </p>
                                    </div>
                                    <ToggleSwitch
                                        isOn={branchCODEnabled ? formData.enableCOD : false}
                                        onToggle={() => {
                                            if (branchCODEnabled) {
                                                setFormData((prev) => ({ ...prev, enableCOD: !prev.enableCOD }));
                                            }
                                        }}
                                        disabled={loading || !branchCODEnabled}
                                        colorOn="bg-emerald-500"
                                        colorOff="bg-gray-300"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 pb-6 flex gap-4">
                            <button
                                onClick={resetModal}
                                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-purple-500/30 transition-all relative overflow-hidden"
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        Saving...
                                    </span>
                                ) : (
                                    isEditing ? "Update" : "Create"
                                )}
                            </button>
                        </div>
                    </Modal>

                    {/* Confirm Delete Modal */}
                    <ConfirmModal
                        visible={confirmModalVisible}
                        message="Are you sure you want to delete this category? This action cannot be undone."
                        onConfirm={confirmDeleteCategory}
                        onCancel={() => {
                            setConfirmModalVisible(false);
                            setCategoryToDelete(null);
                        }}
                        cancelButtonText="Cancel"
                        confirmButtonText="Delete"
                    />
                </>
            )}
        </div>
    );
};

export default CategoryManagement;
